/**
 * Encapsulated companion for blocks/product-card-variant-experience.liquid
 * and snippets/product-card-variant-hero.liquid.
 * Exclusive to "Colección destacada" (sections/product-list.liquid).
 *
 * Three independent behaviors, all scoped to this custom element only:
 *
 * 1. Real-variant panels: toggles the pre-rendered per-variant panels
 *    (gallery, title, description, price, availability, link) in sync with
 *    Horizon's native swatch inputs (data-variant-id), with zero network
 *    requests. Native price/main-image AJAX update for real variants is
 *    untouched — it keeps working exactly as Horizon's
 *    swatches-variant-picker-component already implements it.
 *
 * 2. Gallery thumbnails: clicking a thumbnail swaps the active gallery-set's
 *    main image. Scoped to that gallery-set, never touches another
 *    variant/product's images.
 *
 * 3. Family pills (related products): fetches
 *    sections/section-rendering-collection-sibling-card.liquid for the
 *    clicked product via the Section Rendering API and replaces the entire
 *    .variant-hero region with the response, so gallery, title, price,
 *    description, availability, family pills and the native variant picker
 *    all switch together, atomically, to the newly selected product.
 *
 * Listens for "change" in the capture phase because Horizon's own
 * swatches-variant-picker-component calls event.stopPropagation() on swatch
 * "change" events during the bubble phase; capture runs before that.
 */

/** Must match the opacity transition duration on
 * `.variant-hero__gallery-main img` in snippets/product-card-variant-hero.liquid. */
const THUMB_CROSSFADE_MS = 180;

class ProductCardVariantExperience extends HTMLElement {
  /** @type {AbortController | null} */
  #activeFamilyFetch = null;

  connectedCallback() {
    this.addEventListener('change', this.#handleChange, true);
    this.addEventListener('click', this.#handleThumbClick);
    this.addEventListener('click', this.#handleFamilyPillClick);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange, true);
    this.removeEventListener('click', this.#handleThumbClick);
    this.removeEventListener('click', this.#handleFamilyPillClick);
    this.#activeFamilyFetch?.abort();
  }

  /**
   * @param {Event} event
   */
  #handleChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.type !== 'radio' || !target.dataset.variantId) return;

    this.#setActiveVariant(target.dataset.variantId);
  };

  /**
   * @param {string} variantId
   */
  #setActiveVariant(variantId) {
    const panels = this.querySelectorAll('[data-variant-id]');

    for (const panel of panels) {
      if (panel instanceof HTMLInputElement) continue;
      panel.hidden = panel.dataset.variantId !== variantId;
    }
  }

  /**
   * Swaps the active gallery-set's main image when a thumbnail is clicked,
   * crossfading the swap instead of popping the new src in instantly.
   * @param {MouseEvent} event
   */
  #handleThumbClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('.variant-hero__thumb-btn');
    if (!button) return;

    const gallerySet = button.closest('.variant-hero__gallery-set');
    const mainImage = gallerySet?.querySelector('.variant-hero__gallery-main img');
    if (!(mainImage instanceof HTMLImageElement)) return;

    const fullSrc = button.dataset.fullSrc;
    const fullAlt = button.dataset.fullAlt;

    const applySwap = () => {
      if (fullSrc) mainImage.src = fullSrc;
      if (fullAlt) mainImage.alt = fullAlt;
    };

    if (fullSrc && fullSrc !== mainImage.src && !this.#prefersReducedMotion()) {
      mainImage.classList.add('is-crossfading');
      window.setTimeout(() => {
        applySwap();
        mainImage.classList.remove('is-crossfading');
      }, THUMB_CROSSFADE_MS);
    } else {
      applySwap();
    }

    for (const thumbButton of gallerySet.querySelectorAll('.variant-hero__thumb-btn')) {
      thumbButton.classList.toggle('variant-hero__thumb-btn--active', thumbButton === button);
    }
  };

  #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Handles a click on a related-product pill: fetches the dedicated
   * section-rendering endpoint for that product and replaces the entire
   * hero region with the response, so the card fully becomes that product.
   * Falls back to normal navigation for modifier-key clicks (new tab, etc.)
   * or if the fetch fails.
   * @param {MouseEvent} event
   */
  #handleFamilyPillClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const pill = target.closest('.related-products-pills__pill');
    if (!(pill instanceof HTMLAnchorElement)) return;

    if (pill.classList.contains('related-products-pills__pill--active')) {
      event.preventDefault();
      return;
    }

    if (event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    this.#swapToProduct(pill.href);
  };

  /**
   * @param {string} url
   */
  async #swapToProduct(url) {
    const container = this.querySelector('.variant-hero-container');
    if (!container) return;

    this.#activeFamilyFetch?.abort();
    const controller = new AbortController();
    this.#activeFamilyFetch = controller;

    container.setAttribute('aria-busy', 'true');

    try {
      const requestUrl = `${url}${url.includes('?') ? '&' : '?'}section_id=section-rendering-collection-sibling-card`;
      const response = await fetch(requestUrl, { signal: controller.signal });
      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const newHero = html.querySelector('.variant-hero');
      if (!newHero) throw new Error('No .variant-hero found in the response');

      const reducedMotion = this.#prefersReducedMotion();
      if (!reducedMotion) newHero.classList.add('variant-hero--entering');

      const currentHero = container.querySelector('.variant-hero');
      if (currentHero) {
        currentHero.replaceWith(newHero);
      } else {
        container.appendChild(newHero);
      }

      if (!reducedMotion) {
        // Force a style flush before removing the class so the browser
        // transitions from opacity:0 rather than skipping straight to 1.
        void newHero.offsetWidth;
        newHero.classList.remove('variant-hero--entering');
      }

      this.#updateCardLink(url);
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.warn('[product-card-variant-experience] Failed to switch related product:', error);
      }
    } finally {
      container.removeAttribute('aria-busy');
      if (this.#activeFamilyFetch === controller) {
        this.#activeFamilyFetch = null;
      }
    }
  }

  /**
   * Updates the card's full-cover link so clicking anywhere on the card
   * navigates to the currently selected related product.
   * @param {string} url
   */
  #updateCardLink(url) {
    const productCard = this.closest('product-card');
    const link = productCard?.querySelector('.product-card__link');
    if (link instanceof HTMLAnchorElement) {
      link.href = url;
    }
  }
}

if (!customElements.get('product-card-variant-experience')) {
  customElements.define('product-card-variant-experience', ProductCardVariantExperience);
}
